import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ecourtApi } from './ecourtApi';
import { ecPath, fmtDate, parseCnrText } from './ecourtUtils';
import { Loader, ErrorBox, Empty, PageHeader, Segmented, Cost, ChargeNote, useBalance } from './EcourtUi';

const PAGE = 50;
const asList = (v) => (Array.isArray(v) ? v : []);

const LIST_TYPES = [['', 'All'], ['CIVIL', 'Civil'], ['CRIMINAL', 'Criminal']];
const dateOf = (d) => (d && typeof d === 'object' ? (d.date || d.listDate || '') : String(d || '')).slice(0, 10);
const join = (v) => asList(v).filter(Boolean).join(', ');

function BoardView() {
  const { note } = useBalance();
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [complexes, setComplexes] = useState([]);
  const [courts, setCourts] = useState([]);
  const [dates, setDates] = useState([]);
  const [loc, setLoc] = useState({ state: '', districtCode: '', courtComplexCode: '', court: '' });
  const [dateMode, setDateMode] = useState('day'); // day | range
  const [date, setDate] = useState('');
  const [range, setRange] = useState({ startDate: '', endDate: '' });
  const [filters, setFilters] = useState({ q: '', advocate: '', litigant: '', judge: '', listType: '', courtNo: '', bench: '' });
  const [includeCourtroom, setIncludeCourtroom] = useState(true);
  const [result, setResult] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { ecourtApi.states().then((r) => setStates(asList(r))).catch(setError); }, []);

  useEffect(() => {
    setDistricts([]);
    if (loc.state) ecourtApi.districts(loc.state).then((r) => setDistricts(asList(r))).catch(setError);
  }, [loc.state]);

  useEffect(() => {
    setComplexes([]);
    if (loc.state && loc.districtCode) ecourtApi.complexes(loc.state, loc.districtCode).then((r) => setComplexes(asList(r))).catch(setError);
  }, [loc.state, loc.districtCode]);

  useEffect(() => {
    setCourts([]);
    if (loc.state && loc.districtCode && loc.courtComplexCode) {
      ecourtApi.courts(loc.state, loc.districtCode, loc.courtComplexCode).then((r) => setCourts(asList(r))).catch(setError);
    }
  }, [loc.state, loc.districtCode, loc.courtComplexCode]);

  // Dates that have a published cause list for the picked location (free)
  useEffect(() => {
    setDates([]);
    if (!loc.state) return;
    ecourtApi.availableDates(loc)
      .then((r) => {
        const list = asList(r && !Array.isArray(r) ? (r.dates || r.results) : r).map(dateOf).filter(Boolean);
        setDates(list.slice(0, 14));
        if (list[0]) setDate((d) => d || list[0]);
      })
      .catch(setError);
  }, [loc]);

  const setLevel = (key, value) => {
    const order = ['state', 'districtCode', 'courtComplexCode', 'court'];
    const next = { ...loc, [key]: value };
    order.slice(order.indexOf(key) + 1).forEach((k) => { next[k] = ''; });
    setLoc(next);
    setResult(null);
  };
  const setF = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  const params = (newOffset) => ({
    ...loc,
    ...(dateMode === 'day' ? { date } : range),
    q: filters.q.trim(), advocate: filters.advocate.trim(), litigant: filters.litigant.trim(), judge: filters.judge.trim(),
    listType: filters.listType, courtNo: filters.courtNo.trim(), bench: filters.bench.trim(),
    includeCourtroom: includeCourtroom ? undefined : 'false',
    limit: PAGE, offset: newOffset,
  });

  const search = async (newOffset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const r = await ecourtApi.causeSearch(params(newOffset));
      setResult(note(r));
      setOffset(newOffset);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  // At least one search or filter is required (eCourtsIndia rule)
  const canSearch = Boolean(loc.state || filters.q.trim() || filters.advocate.trim() || filters.litigant.trim() || filters.judge.trim()
    || (dateMode === 'day' ? date : range.startDate || range.endDate));
  const rows = (result && result.results) || [];
  const returned = result ? (result.returnedCount != null ? result.returnedCount : rows.length) : 0;

  return (
    <>
      <form className="ec-card mb-3" onSubmit={(e) => { e.preventDefault(); if (canSearch) search(0); }} noValidate>
        <div className="row g-3">
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="cl-state" className="ec-label">State</label>
            <select id="cl-state" className="form-select" value={loc.state} onChange={(e) => setLevel('state', e.target.value)}>
              <option value="">All states</option>
              {states.map((s) => <option key={s.state} value={s.state}>{s.stateName || s.state}</option>)}
            </select>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="cl-district" className="ec-label">District / High Court</label>
            <select id="cl-district" className="form-select" value={loc.districtCode} disabled={!districts.length} onChange={(e) => setLevel('districtCode', e.target.value)}>
              <option value="">All districts</option>
              {districts.map((d) => <option key={d.districtCode} value={d.districtCode}>{d.districtName}</option>)}
            </select>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="cl-complex" className="ec-label">Court complex</label>
            <select id="cl-complex" className="form-select" value={loc.courtComplexCode} disabled={!complexes.length} onChange={(e) => setLevel('courtComplexCode', e.target.value)}>
              <option value="">All complexes</option>
              {complexes.map((c) => <option key={c.courtComplexCode} value={c.courtComplexCode}>{c.courtComplexName}</option>)}
            </select>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="cl-court" className="ec-label">Court</label>
            <select id="cl-court" className="form-select" value={loc.court} disabled={!courts.length} onChange={(e) => setLevel('court', e.target.value)}>
              <option value="">All courts</option>
              {courts.map((c) => <option key={c.court} value={c.court}>{`Court ${c.courtNo || c.court}${c.judgeName || c.courtName ? ` · ${c.judgeName || c.courtName}` : ''}`}</option>)}
            </select>
          </div>

          <div className="col-12">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
              <span className="ec-label m-0">Date</span>
              <Segmented value={dateMode} onChange={setDateMode} label="Date mode" options={[{ value: 'day', label: 'One day' }, { value: 'range', label: 'Date range' }]} />
            </div>
            {dateMode === 'day' ? (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <input type="date" aria-label="Cause list date" className="form-control ec-date-input" value={date} onChange={(e) => setDate(e.target.value)} />
                {dates.map((d) => (
                  <button key={d} type="button" className={`ec-pill${date === d ? ' active' : ''}`} aria-pressed={date === d} onClick={() => setDate(d)}>{fmtDate(d)}</button>
                ))}
                {date && <button type="button" className="btn btn-link ec-link btn-sm p-0" onClick={() => setDate('')}>Any date</button>}
                {loc.state && !dates.length && <span className="small ec-muted">No published cause lists for this place yet — pick a date or search without one.</span>}
              </div>
            ) : (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <input type="date" aria-label="From date" className="form-control ec-date-input" value={range.startDate} onChange={(e) => setRange({ ...range, startDate: e.target.value })} />
                <span className="ec-muted">to</span>
                <input type="date" aria-label="To date" className="form-control ec-date-input" value={range.endDate} min={range.startDate || undefined} onChange={(e) => setRange({ ...range, endDate: e.target.value })} />
              </div>
            )}
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="cl-q" className="ec-label">Case number or party</label>
            <input id="cl-q" className="form-control" value={filters.q} placeholder="e.g. G.R.case/533/2023 or Ram" onChange={setF('q')} />
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="cl-adv" className="ec-label">Advocate</label>
            <input id="cl-adv" className="form-control" value={filters.advocate} onChange={setF('advocate')} />
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="cl-lit" className="ec-label">Litigant</label>
            <input id="cl-lit" className="form-control" value={filters.litigant} placeholder="e.g. Sharma" onChange={setF('litigant')} />
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="cl-judge" className="ec-label">Judge</label>
            <input id="cl-judge" className="form-control" value={filters.judge} onChange={setF('judge')} />
          </div>
          <div className="col-6 col-md-3 col-xl-2">
            <label htmlFor="cl-type" className="ec-label">List</label>
            <select id="cl-type" className="form-select" value={filters.listType} onChange={setF('listType')}>
              {LIST_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-3 col-xl-2">
            <label htmlFor="cl-courtno" className="ec-label">Court room no.</label>
            <input id="cl-courtno" className="form-control" inputMode="numeric" value={filters.courtNo} placeholder="e.g. 16" onChange={setF('courtNo')} />
          </div>
          <div className="col-6 col-md-3 col-xl-2">
            <label htmlFor="cl-bench" className="ec-label">Bench</label>
            <input id="cl-bench" className="form-control" value={filters.bench} placeholder="e.g. 1" onChange={setF('bench')} />
          </div>
          <div className="col-6 col-md-3 col-xl-3 d-flex align-items-end">
            <div className="form-check mb-2">
              <input className="form-check-input" type="checkbox" id="cl-room" checked={includeCourtroom} onChange={(e) => setIncludeCourtroom(e.target.checked)} />
              <label className="form-check-label" htmlFor="cl-room">Show court room details</label>
            </div>
          </div>
          <div className="col-12 col-xl-3 d-flex align-items-end justify-content-xl-end">
            <button type="submit" className="btn btn-ec px-4 w-100 w-xl-auto" disabled={!canSearch || loading}>
              {loading ? 'Loading…' : 'Show board'}{!loading && <Cost kind="causelist_search" />}
            </button>
          </div>
        </div>
        {!canSearch && <div className="small ec-muted mt-2">Pick a state, a date, or type a case number, party, advocate or judge.</div>}
      </form>

      <ErrorBox error={error} />
      {loading && <Loader label="Loading the board…" />}
      {!loading && result && (rows.length === 0 ? <Empty>No listings match. Try another date or fewer filters.</Empty> : (
        <div className="ec-card p-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-3 pt-3 small ec-muted">
            <span>{offset + 1}–{offset + rows.length} shown{result.query ? ` for “${result.query}”` : ''}</span>
            <ChargeNote resp={result} />
          </div>
          <div className="table-responsive">
            <table className="table ec-table mb-0 align-top">
              <thead><tr><th>Item</th><th>Date</th><th>Case no.</th><th>Parties</th><th>Advocates</th><th>Court & judge</th><th>Stage</th></tr></thead>
              <tbody>
                {rows.map((b) => {
                  const parties = b.party || [join(b.petitioners), join(b.respondents)].filter(Boolean).join(' vs ');
                  const advs = [...asList(b.advocates), ...asList(b.petitionerAdvocates), ...asList(b.respondentAdvocates)].filter(Boolean);
                  return (
                    <tr key={b.id || `${b.date}-${b.listingNo}-${join(b.caseNumber)}`}>
                      <td className="fw-bold ec-accent">{b.listingNo != null ? b.listingNo : '—'}</td>
                      <td className="text-nowrap">{fmtDate(b.date)}</td>
                      <td className="ec-mono small">{join(b.caseNumber) || '—'}{b.listType && <div><span className="ec-badge ec-badge-grey mt-1">{b.listType}</span></div>}</td>
                      <td className="fw-semibold">
                        {b.cnr ? <Link className="ec-link-plain" to={ecPath(`case/${b.cnr}`)}>{parties}</Link> : parties}
                        {(b.district || b.state) && <div className="small ec-muted fw-normal">{[b.district, b.state].filter(Boolean).join(', ')}</div>}
                      </td>
                      <td className="small ec-muted">{advs.slice(0, 3).join(', ') || '—'}</td>
                      <td className="small">
                        {b.courtName && b.courtName !== 'UNKNOWN' ? b.courtName : ''}
                        {b.courtNo ? <div className="ec-muted">Court {b.courtNo}{b.bench ? ` · Bench ${b.bench}` : ''}</div> : null}
                        {join(b.judge) && <div className="ec-muted">{join(b.judge)}</div>}
                      </td>
                      <td className="small">{b.status || b.listingFor || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end gap-2 p-2">
            <button type="button" className="btn btn-sm btn-ec-outline" disabled={offset === 0 || loading} onClick={() => search(Math.max(0, offset - PAGE))}>← Prev</button>
            <button type="button" className="btn btn-sm btn-ec-outline" disabled={returned < PAGE || loading} onClick={() => search(offset + PAGE)}>Next →</button>
          </div>
        </div>
      ))}
    </>
  );
}

function BatchView() {
  const { note } = useBalance();
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const preview = parseCnrText(text);


  const check = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = note(await ecourtApi.cnrBatch(preview.valid));
      setRows(asList(r.results));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="ec-card mb-3">
        <label htmlFor="cb-cnrs" className="ec-label">CNRs to check (up to 100)</label>
        <textarea id="cb-cnrs" className="form-control ec-mono" rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={'JHBO010001232024\nDLND020047882015'} />
        <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
          <span className="ec-badge ec-badge-green">{preview.valid.length} valid</span>
          {preview.invalid.length > 0 && <span className="ec-badge ec-badge-red">{preview.invalid.length} invalid</span>}
          <span className="small ec-muted">District and taluka courts.</span>
          <span className="ms-auto" />
          <button type="button" className="btn btn-ec" disabled={!preview.valid.length || preview.valid.length > 100 || loading} onClick={check}>
            {loading ? 'Checking…' : 'Check listings'}{!loading && preview.valid.length > 0 && <Cost kind="cnr_check" qty={preview.valid.length} />}
          </button>
        </div>
      </div>
      <ErrorBox error={error} />
      {rows && rows.map((r) => {
        const n = r.nextListing;
        return (
          <div className="ec-card ec-card-tight mb-2 d-flex flex-wrap justify-content-between align-items-center gap-2" key={r.cnr}>
            <div>
              <Link to={ecPath(`case/${r.cnr}`)} className="ec-mono ec-link-plain">{r.cnr}</Link>
              <div className="small ec-muted">
                {n ? `${fmtDate(n.date)} · ${n.courtName || ''} · Court ${n.courtNo || '—'} · Item ${n.listingNo != null ? n.listingNo : '—'}${n.listingFor ? ` · ${n.listingFor}` : ''}` : 'No upcoming listing'}
              </div>
            </div>
            <span className={`ec-badge ${r.hasCauselist ? 'ec-badge-yellow' : 'ec-badge-grey'}`}>{r.hasCauselist ? 'Listed' : 'Not listed'}</span>
          </div>
        );
      })}
    </>
  );
}

export default function CauseList() {
  const [mode, setMode] = useState('board');
  return (
    <div>
      <PageHeader title="Cause List" subtitle="Daily boards for any court, or see which of your cases are listed.">
        <Segmented value={mode} onChange={setMode} label="Cause list mode"
          options={[{ value: 'board', label: 'Court board' }, { value: 'batch', label: 'My cases listed?' }]} />
      </PageHeader>
      {mode === 'board' ? <BoardView /> : <BatchView />}
    </div>
  );
}